import { computed, inject, Injectable } from '@angular/core';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { FormsService } from '../forms/forms.service';
import { WidthsService } from '../widths/widths.service';
import { MaterialsService } from '../materials/materials.service';
import { DesignsService } from '../designs/designs.service';
import { SizesService } from '../sizes/sizes.service';
import { RulesService } from '../rules/rules.service';
import { assert, assertTrue } from 'src/app/utils/assert.util';
import { Size } from 'src/app/interfaces/size';
import { Rule } from 'src/app/interfaces/rule';
import { Attribute } from 'src/app/interfaces/attribute';
import { B2BFingerSize } from 'src/app/interfaces/b2b-finger-size';
import { doesMaterialSupportBP, formatFingerSizeForB2BSKU } from 'src/app/utils/configured-item.util';
import { StoneTypesService } from '../stone-types/stone-types.service';
import { StoneShapesService } from '../stone-shapes/stone-shapes.service';
import { StoneArrangementsService } from '../stone-arrangements/stone-arrangements.service';
import { StoneSettingsService } from '../stone-settings/stone-settings.service';
import { StoneTypesWithShapesService } from '../stone-types-with-shapes/stone-types-with-shapes.service';
import { StoneArrangementsWithSettingsService } from '../stone-arrangementsWithSettings/stone-arrangements-with-settings.service';
import { StoneSizesService } from '../stone-sizes/stone-sizes.service';

@Injectable({
  providedIn: 'root'
})
export class ConfiguredItemService {
  formsService = inject(FormsService);
  widthsService = inject(WidthsService);
  materialsService = inject(MaterialsService);
  designsService = inject(DesignsService);
  sizesService = inject(SizesService);
  rulesService = inject(RulesService);
  stoneTypeService = inject(StoneTypesService);
  stoneShapeService = inject(StoneShapesService);
  stoneArrangementService = inject(StoneArrangementsService);
  stoneSettingsService = inject(StoneSettingsService);
  stoneTypesWithShapesService = inject(StoneTypesWithShapesService);
  stoneArrangementsWithSettingsService = inject(StoneArrangementsWithSettingsService);
  stoneSizesService = inject(StoneSizesService);

  readonly keysToCheck: (keyof ConfiguredItem)[] = ['design', 'form', 'primaryMaterial', 'secondaryMaterial', 'width', 'size'];

  readonly ruleKeyMap: Record<string, keyof Rule> = {
    design: 'd',
    form: 'f',
    primaryMaterial: 'im',
    secondaryMaterial: 'om',
    width: 'w',
    size: 's'
  };

  buildHaloSKU(haloStyleNumber: string, size: Size | number, roundToHalf: boolean = true) {
    const b2bFingerSize = this.getB2BFingerSizeObject(size, roundToHalf);
    const haloItemNumber = `${haloStyleNumber}${this.formatSizeForHaloItemNumber(b2bFingerSize)}`;
    return haloItemNumber;
  }

  formatSizeForHaloItemNumber(b2bFingerSize: B2BFingerSize) {
    if (b2bFingerSize.size < 10) {
      return `0${b2bFingerSize.size}`;
    }
    return b2bFingerSize.size.toString();
  }

  getB2BFingerSizeObject(size: Size | number, roundToHalf: boolean = true): B2BFingerSize {
    const sizeValue = typeof size === 'object' ? size.value : size;
    if (roundToHalf && (sizeValue - 0.75 === Math.floor(sizeValue) || sizeValue - 0.25 === Math.floor(sizeValue))) {
      return {
        size: sizeValue - 0.25,
        isQuarterSize: true
      }
    }
    return {
      size: sizeValue,
      isQuarterSize: false
    };
  }

  compareConfiguredItemValidItem(configuredItem: ConfiguredItem, validItem: Rule, keyToExclude: string, conflicts: string[]) {
    const filteredKeysToCheck = this.keysToCheck.filter(k => k !== keyToExclude && !conflicts.includes(k));
    const result = filteredKeysToCheck.reduce((flag, k) => {
      const itemVal = (configuredItem[k] as Attribute).id //getConfiguredItemValue(configuredItem, k);
      return flag && (!itemVal || validItem[this.ruleKeyMap[k]] === itemVal)
    }, true);
    return result;
  }

  isCustomSKU(styleNumberOrSKU: string) {
    const [designOrStones, ...splitStyleNumberOrSKU] = styleNumberOrSKU.split('-');

     if (designOrStones.length !== 5 || designOrStones.match(/[a-zA-Z]/)) {
      splitStyleNumberOrSKU.unshift(designOrStones);
    }

    if (!(splitStyleNumberOrSKU.length >= 3 && splitStyleNumberOrSKU.length <= 5)) {
      return false;
    }

    return splitStyleNumberOrSKU.length === 5 || !splitStyleNumberOrSKU[3].match(/[a-zA-Z]/);
  }

  isCustomStyle(styleNumberOrSKU: string) {
    const [designOrStones, ...splitStyleNumberOrSKU] = styleNumberOrSKU.split('-');
    if (designOrStones.length !== 5 || designOrStones.match(/[a-zA-Z]/)) {
      splitStyleNumberOrSKU.unshift(designOrStones);
    }

    return splitStyleNumberOrSKU.length === 4 && splitStyleNumberOrSKU[3].match(/[a-zA-Z]/);
  }

  getAttributesFromNewSKU(sku: string) {
    const attributesFromStyle = this.getAttributesFromNewStyleNumber(sku);

    const size = sku.split('-').pop();
    const matchingFingerSize = this.sizesService.values().find(fingerSize => size === formatFingerSizeForB2BSKU(fingerSize.value));
    assert(matchingFingerSize, 'Failed to match size in sku');

    attributesFromStyle.size = matchingFingerSize;
    return attributesFromStyle;
  }

  getAttributesFromNewStyleNumber(styleNumber: string) {
    const [designOrStones, ...remainingStyleParts] = styleNumber.split('-')
    if (designOrStones.length !== 5 || designOrStones.match(/[a-zA-Z]/)) {
      remainingStyleParts.unshift(designOrStones)
    }
    const stones = designOrStones.length === 5 && !designOrStones.match(/[a-zA-Z]/) ? designOrStones : null;
    const [design, form, width, materials] = remainingStyleParts;

    const matchingForm = this.formsService.values().find(f => form === f.shortName);
    const matchingWidth = this.widthsService.values().find(w => width === w.shortName);
    const allPrimaryMaterials = this.materialsService.values().filter(material => material.type === 'Primary');
    const matchingPrimaryMaterial = allPrimaryMaterials.find(m => materials.startsWith(m.shortName));

    assert(matchingPrimaryMaterial, 'Failed to match primary material in sku');
    const leftoverMaterial = materials.replace(matchingPrimaryMaterial.shortName, '');
    const allSecondaryMaterials = this.materialsService.values().filter(material => material.type === 'Secondary');
    const matchingSecondaryMaterial = leftoverMaterial.length > 0 ? allSecondaryMaterials.find(m => leftoverMaterial === m.shortName) : allSecondaryMaterials.find(m => materials === m.shortName);

    assert(matchingForm, 'Failed to match form in sku');
    assert(matchingWidth, 'Failed to match width in sku');
    assert(matchingSecondaryMaterial, 'Failed to match secondary material in sku');

    const doesCenterSupportBP = doesMaterialSupportBP(matchingSecondaryMaterial ?? matchingPrimaryMaterial);
    const matchingDesign = doesCenterSupportBP ? this.designsService.values().find(d => design === d.shortName) : (this.designsService.values().find(d => design === d.shortName) ?? this.designsService.values().find(d => `${design}BP` === d.shortName));
    assert(matchingDesign, 'Failed to match design in sku');

    const configuredItem: ConfiguredItem = {
      design: matchingDesign,
      form: matchingForm,
      width: matchingWidth,
      primaryMaterial: matchingPrimaryMaterial,
      secondaryMaterial: matchingSecondaryMaterial,
      engravingText: null,
      engravingType: null,
      size: null,
      gemstoneType: null,
      gemstoneArrangement: null,
      gemstoneSize: null,
      gemstoneSetting: null,
      gemstoneShape: null
    };

    if (stones) {
      const stoneParts = stones?.match((/(?<typeWithShape>\d)(?<arrangementWithSetting>\d{2})(?<size>\d{2})/));
      const { typeWithShape, arrangementWithSetting, size } = stoneParts?.groups ?? {};

      const matchedTypeWithShape = this.stoneTypesWithShapesService.values().find(ss => ss.shortName === typeWithShape);
      const matchedArrangementWithSetting = this.stoneArrangementsWithSettingsService.values().find(sas => sas.shortName === arrangementWithSetting);
      const matchedSize = this.stoneSizesService.values().find(s => s.shortName === size);

      assertTrue(!!matchedTypeWithShape && !!matchedArrangementWithSetting && !!matchedSize, 'Failed to match stone parts in style');

      const type = this.stoneTypeService.values().find(t => t.id === matchedTypeWithShape.stoneTypeID);
      const shape = this.stoneShapeService.values().find(s => s.id === matchedTypeWithShape.stoneShapeID);
      const arrangement = this.stoneArrangementService.values().find(a => a.id === matchedArrangementWithSetting.stoneArrangementID);
      const setting = this.stoneSettingsService.values().find(s => s.id === matchedArrangementWithSetting.stoneSettingID);

      assert(!!type && !!shape && !!arrangement && !!setting, 'Failed to find stone part with id');

      return Object.assign({}, configuredItem, { gemstoneType: type, gemstoneShape: shape, gemstoneArrangement: arrangement, gemstoneSetting: setting, gemstoneSize: matchedSize });
    }

    return configuredItem;
  }

  getStyleFromCustomSKU(sku: string) {
    return sku.split('-').slice(0, -1).join('-');
  }

  getNewSKUFromSelectedAttributes(selectedAttributes: ConfiguredItem) {
    const skuParts = this.getNewStyleFromSelectedAttributes(selectedAttributes);

    assert(selectedAttributes.size, 'No selected finger size');
    const sku = `${skuParts}-${formatFingerSizeForB2BSKU(selectedAttributes.size.value)}`;
    return sku;
  }

  getNewStyleFromSelectedAttributes(selectedAttributes: ConfiguredItem) {
    assert(selectedAttributes.form, 'No selected form');
    assert(selectedAttributes.width, 'No selected width');
    assert(selectedAttributes.primaryMaterial, 'No selected primary material');
    assert(selectedAttributes.design, 'No selected design.')

    const styleParts = [
      selectedAttributes.design.displayName.includes('VDR') || doesMaterialSupportBP(selectedAttributes.secondaryMaterial ?? selectedAttributes.primaryMaterial) ? selectedAttributes.design.shortName : selectedAttributes.design.shortName.replace(/[bB][pP]/, ''),
      selectedAttributes.form.shortName,
      selectedAttributes.width.shortName,
      [selectedAttributes.primaryMaterial.shortName, selectedAttributes.secondaryMaterial?.shortName !== selectedAttributes.primaryMaterial.shortName ? (selectedAttributes?.secondaryMaterial?.shortName ?? '') : ''].join(''),
    ];

    if (selectedAttributes.gemstoneArrangement && selectedAttributes.gemstoneType && selectedAttributes.gemstoneShape && selectedAttributes.gemstoneSetting && selectedAttributes.gemstoneSize) {
      const arrangementsWithSettings = this.stoneArrangementsWithSettingsService.values().find(sas => sas.stoneArrangementID === selectedAttributes.gemstoneArrangement?.id && sas.stoneSettingID === selectedAttributes.gemstoneSetting?.id);
      const typeWithShape = this.stoneTypesWithShapesService.values().find(sts => sts.stoneTypeID === selectedAttributes.gemstoneType?.id && sts.stoneShapeID === selectedAttributes.gemstoneShape?.id);

      styleParts.unshift([typeWithShape?.shortName, arrangementsWithSettings?.shortName, selectedAttributes.gemstoneSize.shortName].join(''));
    }

    const styleNumber = styleParts.join('-');
    return styleNumber;
  }

}
